import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();
  const [loanAmount, setLoanAmount] = useState(500000);
  const [tenure, setTenure] = useState(24);
  const [interestRate, setInterestRate] = useState(6);

  const calculateEMI = () => {
    const principal = loanAmount;
    const ratePerMonth = interestRate / (12 * 100);
    const months = tenure;
    const emi = principal * ratePerMonth * Math.pow(1 + ratePerMonth, months) / (Math.pow(1 + ratePerMonth, months) - 1);
    return isNaN(emi) ? 0 : emi;
  };

  const emi = calculateEMI();
  const totalPayable = emi * tenure;
  const totalInterest = totalPayable - loanAmount;

  const handleApplyClick = () => {
    navigate('/apply');
  };

  const handleCheckStatus = () => {
    navigate('/check-status');
  };

  const tenureOptions = [12, 24, 36, 48, 60, 72, 84, 96, 108, 120];

  return (
    <>
      <section className="hero-section">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <div className="hero-badge">Trusted by 1M+ Customers</div>
              <h1 className="hero-title">
                Get Instant Loan <br />
                <span className="gradient-text">Up to ₹50 Lakhs</span>
              </h1>
              <p className="hero-description">
                India's fastest digital lending platform. Get approved in 2 minutes, 
                money in account within 48 hours. Interest rates from 3% to 9% per annum.
              </p>
              <div className="hero-stats">
                <div className="stat">
                  <div className="stat-number">1M+</div>
                  <div className="stat-label">Happy Customers</div>
                </div>
                <div className="stat">
                  <div className="stat-number">₹5000Cr+</div>
                  <div className="stat-label">Disbursed</div>
                </div>
                <div className="stat">
                  <div className="stat-number">98%</div>
                  <div className="stat-label">Approval Rate</div>
                </div>
              </div>
              <div className="hero-buttons">
                <button onClick={handleApplyClick} className="btn-hero">
                  Apply for Loan Now →
                </button>
                <button onClick={handleCheckStatus} className="btn-hero-secondary">
                  Check Status →
                </button>
              </div>
              <p className="hero-note">No hidden charges | Instant approval</p>
            </div>
            
            <div className="hero-calculator">
              <div className="calculator-card">
                <h3>Loan EMI Calculator</h3>
                <div className="calc-input-group">
                  <label>Loan Amount (₹)</label>
                  <input 
                    type="range" 
                    min="10000" 
                    max="5000000" 
                    step="10000"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                  />
                  <div className="range-value">₹{loanAmount.toLocaleString('en-IN')}</div>
                </div>
                <div className="calc-input-group">
                  <label>Interest Rate (% p.a.)</label>
                  <input 
                    type="range" 
                    min="3" 
                    max="9" 
                    step="0.5"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                  />
                  <div className="range-value">{interestRate}%</div>
                </div>
                <div className="calc-input-group">
                  <label>Tenure (Months)</label>
                  <div className="tenure-buttons">
                    {tenureOptions.map(t => (
                      <button 
                        key={t}
                        className={`tenure-btn ${tenure === t ? 'active' : ''}`}
                        onClick={() => setTenure(t)}
                      >
                        {t}M
                      </button>
                    ))}
                  </div>
                </div>
                <div className="calc-result">
                  <div className="result-item">
                    <span>Monthly EMI</span>
                    <strong>₹{emi.toFixed(2)}</strong>
                  </div>
                  <div className="result-item">
                    <span>Interest Rate</span>
                    <strong>{interestRate}% p.a.</strong>
                  </div>
                  <div className="result-item">
                    <span>Total Interest</span>
                    <strong>₹{totalInterest.toFixed(2)}</strong>
                  </div>
                  <div className="result-item total">
                    <span>Total Payment</span>
                    <strong>₹{totalPayable.toFixed(2)}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Loan Products Section */}
      <section className="products-section" id="loans">
        <div className="container">
          <div className="section-header">
            <h2>Loan Products We Offer</h2>
            <p>Choose the perfect loan that fits your needs</p>
          </div>
          <div className="products-grid">
            <div className="product-card">
              <div className="product-icon">🏠</div>
              <h3>Home Loan</h3>
              <div className="product-rate">3% - 8.5% p.a.</div>
              <p>Up to ₹5 Crore | Up to 30 Years</p>
              <button className="btn-outline" onClick={handleApplyClick}>Apply Now</button>
            </div>
            <div className="product-card featured">
              <div className="popular-badge">Most Popular</div>
              <div className="product-icon">🚗</div>
              <h3>Car Loan</h3>
              <div className="product-rate">3% - 9% p.a.</div>
              <p>Up to ₹1 Crore | Up to 7 Years</p>
              <button className="btn-primary" onClick={handleApplyClick}>Apply Now</button>
            </div>
            <div className="product-card">
              <div className="product-icon">💼</div>
              <h3>Personal Loan</h3>
              <div className="product-rate">3% - 12% p.a.</div>
              <p>Up to ₹50 Lakhs | Up to 5 Years</p>
              <button className="btn-outline" onClick={handleApplyClick}>Apply Now</button>
            </div>
            <div className="product-card">
              <div className="product-icon">📚</div>
              <h3>Education Loan</h3>
              <div className="product-rate">3% - 10% p.a.</div>
              <p>Up to ₹1.5 Crore | Up to 15 Years</p>
              <button className="btn-outline" onClick={handleApplyClick}>Apply Now</button>
            </div>
            <div className="product-card">
              <div className="product-icon">🏢</div>
              <h3>Business Loan</h3>
              <div className="product-rate">3% - 11% p.a.</div>
              <p>Up to ₹2 Crore | Up to 10 Years</p>
              <button className="btn-outline" onClick={handleApplyClick}>Apply Now</button>
            </div>
          </div>
        </div>
      </section>

      {/* Rest of the sections remain same */}
      <section className="features-section" id="features">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose LoanInHand?</h2>
            <p>We make borrowing simple, transparent, and hassle-free</p>
          </div>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">⚡</div>
              <h3>Instant Approval</h3>
              <p>Get loan approval within 2 minutes with our AI-powered system</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💰</div>
              <h3>Lowest Interest Rates</h3>
              <p>Starting from just 3% to 9% per annum with flexible repayment options</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📱</div>
              <h3>100% Digital Process</h3>
              <p>Complete online process with minimal documentation</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🛡️</div>
              <h3>Secure & Safe</h3>
              <p>Your data is protected with bank-level encryption</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🎯</div>
              <h3>Flexible Tenure</h3>
              <p>Choose repayment tenure from 6 to 120 months</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🤝</div>
              <h3>No Hidden Charges</h3>
              <p>Transparent process with zero hidden fees</p>
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Get your loan in 3 simple steps</p>
          </div>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-icon">📝</div>
              <h3>Fill Application</h3>
              <p>Enter your mobile number and complete the application</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-icon">✅</div>
              <h3>Instant Verification</h3>
              <p>Get verified instantly with our AI system</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-icon">💵</div>
              <h3>Get Money</h3>
              <p>Amount credited within 48 hours</p>
            </div>
          </div>
        </div>
      </section>

      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2>What Our Customers Say</h2>
            <p>Join thousands of satisfied customers</p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="rating">★★★★★</div>
              <p>"Best experience ever! Got my loan approved in just 2 hours. Highly recommended!"</p>
              <div className="customer">
                <strong>Rahul Sharma</strong>
                <span>Delhi</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="rating">★★★★★</div>
              <p>"Lowest interest rates and fastest disbursal. Thank you LoanInHand!"</p>
              <div className="customer">
                <strong>Priya Patel</strong>
                <span>Mumbai</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="rating">★★★★★</div>
              <p>"The entire process was digital and hassle-free. Great customer support."</p>
              <div className="customer">
                <strong>Amit Kumar</strong>
                <span>Bangalore</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section" id="faq">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Got questions? We've got answers</p>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>What is the minimum credit score required?</h3>
              <p>Minimum CIBIL score of 650 is required for loan approval.</p>
            </div>
            <div className="faq-item">
              <h3>How long does the approval take?</h3>
              <p>Initial approval takes 2 minutes, and final disbursal within 48 hours.</p>
            </div>
            <div className="faq-item">
              <h3>What documents are required?</h3>
              <p>PAN card, Aadhar card, bank statements, and income proof.</p>
            </div>
            <div className="faq-item">
              <h3>Can I prepay my loan?</h3>
              <p>Yes, with minimal prepayment charges after 6 months.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Your Loan?</h2>
            <p>Join 1 million+ happy customers who trusted LoanInHand</p>
            <button onClick={handleApplyClick} className="btn-cta">
              Apply for Loan Now →
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;